import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ZoomIn } from 'lucide-react';
import axios from 'axios';
import ImageLightbox from '../components/ImageLightbox';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Fallback static gallery data (used if API fails)
  const fallbackImages = [
    { id: 1, title: 'Advanced Robotics Lab Setup', description: 'State-of-the-art robotics laboratory with cutting-edge equipment', image_url: '/gallery/robotics/hitech-lab-robotics-setup-1.png', category: 'Robotics Lab', order: 1 },
    { id: 2, title: 'Robotic Arm Installation', description: 'Industrial robotic arm for automation and control systems', image_url: '/gallery/robotics/8kg.jpg', category: 'Robotics Lab', order: 2 },
    { id: 3, title: 'UGV Beast Platform', description: 'Unmanned Ground Vehicle with ROS2 integration', image_url: '/gallery/robotics/ugv-beast-pt-pi5-ros2-kit-1_1.webp', category: 'Robotics Lab', order: 3 },
    { id: 4, title: 'Robotics Training Session', description: 'Students learning advanced robotics programming', image_url: '/gallery/robotics/WhatsApp Image 2026-02-14 at 16.09.07.jpeg', category: 'Robotics Lab', order: 4 },
    { id: 5, title: 'Robot Lab Equipment', description: 'Comprehensive robotics equipment and testing stations', image_url: '/gallery/robotics/WhatsApp Image 2026-02-14 at 16.09.07 (1).jpeg', category: 'Robotics Lab', order: 5 },
    { id: 6, title: 'Advanced Robot Systems', description: 'High-tech robot systems for research and development', image_url: '/gallery/robotics/WhatsApp Image 2026-02-14 at 16.09.07 (2).jpeg', category: 'Robotics Lab', order: 6 },
    { id: 7, title: 'AR/VR Integration', description: 'Augmented and Virtual Reality systems integration', image_url: '/gallery/robotics/image.webp', category: 'AR/VR Lab', order: 7 },
    { id: 8, title: 'Autonomous Robot Platform', description: 'Outdoor autonomous robotics platform with drone integration', image_url: '/gallery/robotics/robot-stands-rocky-surface-holding-drone-as-sun-sets-casting-warm-light-across-sky-robot-figure-standing-412566932.webp', category: 'Robotics Lab', order: 8 },
    { id: 9, title: 'Drone Soccer Arena Setup', description: 'Professional drone soccer arena with safety nets and equipment', image_url: '/gallery/arena/WhatsApp Image 2026-02-13 at 15.19.41.jpeg', category: 'Drone Soccer', order: 9 },
    { id: 10, title: 'Arena Safety Systems', description: 'Complete safety cage and protection systems for drone sports', image_url: '/gallery/arena/WhatsApp Image 2026-02-13 at 15.19.41 (1).jpeg', category: 'Drone Soccer', order: 10 },
    { id: 11, title: 'Drone Soccer Training', description: 'Training facility for drone soccer competitions', image_url: '/gallery/arena/WhatsApp Image 2026-02-13 at 15.19.42.jpeg', category: 'Drone Soccer', order: 11 },
    { id: 12, title: 'Competition Arena', description: 'Regulation-size drone soccer competition arena', image_url: '/gallery/arena/WhatsApp Image 2026-02-13 at 15.19.42 (1).jpeg', category: 'Drone Soccer', order: 12 },
  ];

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API_URL}/gallery`);
        if (response.data && response.data.length > 0) {
          setGalleryItems(response.data);
        } else {
          setGalleryItems(fallbackImages);
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setGalleryItems(fallbackImages);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = ['all', ...new Set(galleryItems.map((item) => item.category))];
  const filteredItems =
    selectedCategory === 'all'
      ? galleryItems
      : galleryItems.filter((item) => item.category === selectedCategory);

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-xl font-heading text-primary">Loading Gallery...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <SEO 
        title="Gallery - UAV Geo Solutions | Lab Installations & Projects"
        description="View our portfolio of successful lab installations including Robotics Labs, Drone Labs, AR/VR Labs, Drone Soccer Arenas, and RPTO Training Centers across India."
        keywords="drone lab gallery, robotics lab photos, drone soccer arena, RPTO training center, technology lab installations India"
        url="/gallery"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-heading font-bold uppercase tracking-tighter mb-6 text-glow">
            Lab Installations Gallery
          </h1>
          <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-3xl mx-auto">
            Showcasing our successful lab installations across institutions
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-6 py-3 rounded-full uppercase text-xs font-bold tracking-wider transition-all ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-primary to-indigo-500 text-black shadow-lg shadow-primary/30'
                  : 'glass text-gray-400 hover:text-primary hover:border-primary/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <Search size={64} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No items found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                data-testid={`gallery-item-${index}`}
                className="group relative glass rounded-2xl overflow-hidden hover-lift cursor-pointer border border-white/5 hover:border-primary/30"
                onClick={() => handleImageClick(filteredItems.indexOf(item))}
              >
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <ZoomIn size={48} className="text-primary" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-400 line-clamp-2">{item.description}</p>
                  <p className="text-xs text-primary uppercase tracking-wider mt-4">{item.category}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <ImageLightbox
            images={filteredItems}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
